import React, { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listFavorites } from "../api/dealflowApi";
import DealCard from "../components/DealCard";

/**
 * Sprint M Item 5 — i18n Phase 2: all visible strings come from the
 * `favorites` namespace (DE/EN/HR).
 */
export default function FavoritesPage() {
  const { t } = useTranslation("favorites");
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: ["favorites"],
    queryFn: ({ pageParam }) => listFavorites({ cursor: pageParam, limit: 24 }),
    getNextPageParam: (last) => last.next_cursor || undefined,
    initialPageParam: null,
  });

  const items = useMemo(
    () => data?.pages.flatMap((p) => p.items) || [],
    [data],
  );

  return (
    <div data-testid="favorites-page" className="space-y-6">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h1 className="text-2xl text-zinc-100 font-medium tracking-tight">
          {t("page.heading")}
        </h1>
        <Link
          to="/dashboard"
          data-testid="favorites-back-link"
          className="text-sm text-zinc-400 hover:text-zinc-200 underline-offset-4 hover:underline"
        >
          {t("page.back_to_dealflow")}
        </Link>
      </div>

      {isLoading ? (
        <div className="text-zinc-500">{t("page.loading")}</div>
      ) : isError ? (
        <div className="text-rose-400">
          {t("page.error_prefix")}: {String(error?.message || error)}
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="favorites-empty"
          className="border border-zinc-800 bg-zinc-950/60 rounded-2xl p-10 text-center"
        >
          <div className="text-sm uppercase tracking-widest text-amber-400/70 mb-3">
            {t("empty.label")}
          </div>
          <h3 className="text-xl text-zinc-100 mb-2">
            {t("empty.title")}
          </h3>
          <p className="text-sm text-zinc-400 mb-6 max-w-prose mx-auto">
            {t("empty.description")}
          </p>
          <Link
            to="/dashboard"
            className="inline-block px-4 py-2 rounded-lg bg-amber-500 text-zinc-950 text-sm font-medium hover:bg-amber-400"
          >
            {t("empty.cta")}
          </Link>
        </div>
      ) : (
        <>
          <div
            data-testid="favorites-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {items.map((c) => (
              <DealCard key={c.id} card={c} isFavorite />
            ))}
          </div>
          {hasNextPage && (
            <div className="text-center pt-4">
              <button
                data-testid="favorites-load-more-btn"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="px-5 py-2 rounded-lg border border-zinc-700 text-zinc-200 hover:border-amber-500/60 hover:text-amber-300 disabled:opacity-50"
              >
                {isFetchingNextPage ? t("page.loading") : t("page.load_more")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
